/**
 * Grid geometry: directions, ring rotation, distances, lines and targeting
 * shapes. Pure functions over positions; nothing here knows about units'
 * stats. The rules are on the wiki page Architecture → game-core contract,
 * section Targeting.
 */
import type { Targeting } from '@ab/content-schema';

export type Pos = { readonly x: number; readonly y: number };

/** `n` is +y. Order matters: it is the tie-break order everywhere. */
export const dirs = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
export type Dir = (typeof dirs)[number];

export const dirOffset: Readonly<Record<Dir, Pos>> = {
  n: { x: 0, y: 1 },
  ne: { x: 1, y: 1 },
  e: { x: 1, y: 0 },
  se: { x: 1, y: -1 },
  s: { x: 0, y: -1 },
  sw: { x: -1, y: -1 },
  w: { x: -1, y: 0 },
  nw: { x: -1, y: 1 },
};

export const isDir = (value: unknown): value is Dir => typeof value === 'string' && (dirs as readonly string[]).includes(value);

export const samePos = (a: Pos, b: Pos): boolean => a.x === b.x && a.y === b.y;
export const add = (a: Pos, b: Pos): Pos => ({ x: a.x + b.x, y: a.y + b.y });
export const manhattan = (a: Pos, b: Pos): number => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
export const chebyshev = (a: Pos, b: Pos): number => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

/** The direction from `from` to an adjacent (8-neighbour) `to`, or null if not adjacent. */
export const dirTo = (from: Pos, to: Pos): Dir | null => {
  for (const d of dirs) if (samePos(add(from, dirOffset[d]), to)) return d;
  return null;
};

/** The direction whose offset best matches the vector from `from` to `to` (sign of each axis). */
export const dirToward = (from: Pos, to: Pos): Dir | null => {
  const dx = Math.sign(to.x - from.x);
  const dy = Math.sign(to.y - from.y);
  if (dx === 0 && dy === 0) return null;
  for (const d of dirs) if (dirOffset[d].x === dx && dirOffset[d].y === dy) return d;
  return null;
};

/** Steps of 45° from `n` to `facing`, clockwise. */
const turnsFromNorth = (facing: Dir): number => dirs.indexOf(facing);

/**
 * Ring rotation. An offset at Chebyshev distance r sits on a ring of 8r cells;
 * rotating by one 45° step moves it r cells clockwise along that ring. For
 * cardinal facings this is the ordinary 90° rotation; for diagonals it is the
 * only integer rotation that keeps ring distance. Offsets are written for a
 * user facing `n`.
 */
export const rotateOffset = (offset: Pos, facing: Dir): Pos => {
  const r = Math.max(Math.abs(offset.x), Math.abs(offset.y));
  if (r === 0) return offset;
  const k = turnsFromNorth(facing);
  if (k === 0) return offset;
  const ring = ringCells(r);
  const index = ring.findIndex((c) => samePos(c, offset));
  const shifted = (index + k * r) % ring.length;
  return ring[shifted] ?? offset;
};

/** The 8r cells of the ring at Chebyshev distance r, clockwise starting from (0, r) (north). */
export const ringCells = (r: number): Pos[] => {
  const cells: Pos[] = [];
  // Start at (0, r), north, so index 0 is direction `n`; then clockwise.
  for (let x = 0; x <= r; x += 1) cells.push({ x, y: r });
  for (let y = r - 1; y >= -r; y -= 1) cells.push({ x: r, y });
  for (let x = r - 1; x >= -r; x -= 1) cells.push({ x, y: -r });
  for (let y = -r + 1; y <= r; y += 1) cells.push({ x: -r, y });
  for (let x = -r + 1; x < 0; x += 1) cells.push({ x, y: r });
  return cells;
};

/** A grid the geometry can ask about. Implemented by the floor. */
export type GridView = {
  readonly width: number;
  readonly height: number;
  /** True when a unit can stand on the tile. */
  isFloor(pos: Pos): boolean;
  /** The id of the unit on the tile, or null. */
  unitAt(pos: Pos): string | null;
  /** The room containing the tile, or null in corridors. */
  roomAt(pos: Pos): number | null;
  /** All interior tiles of a room. */
  roomTiles(room: number): readonly Pos[];
};

export const inBounds = (g: { readonly width: number; readonly height: number }, p: Pos): boolean =>
  p.x >= 0 && p.y >= 0 && p.x < g.width && p.y < g.height;

/**
 * The corner rule: true for an orthogonal direction; for a diagonal, true when
 * both orthogonal neighbours of `from` on the way (`from + (dx, 0)` and
 * `from + (0, dy)`) are non-wall. A diagonal step and a diagonal basic attack
 * need it.
 */
export const cornerClear = (g: GridView, from: Pos, d: Dir): boolean => {
  const off = dirOffset[d];
  if (off.x === 0 || off.y === 0) return true;
  const a = { x: from.x + off.x, y: from.y };
  const b = { x: from.x, y: from.y + off.y };
  return inBounds(g, a) && inBounds(g, b) && g.isFloor(a) && g.isFloor(b);
};

/**
 * Whether a unit at `from` may step in direction `d`: the target must be a
 * free floor tile, and a diagonal step needs both orthogonal neighbours to be
 * floor (no corner cutting, `cornerClear`).
 */
export const canStep = (g: GridView, from: Pos, d: Dir): boolean => {
  const to = add(from, dirOffset[d]);
  if (!inBounds(g, to) || !g.isFloor(to) || g.unitAt(to) !== null) return false;
  return cornerClear(g, from, d);
};

/** Tiles along a line from `from` in direction `d`, up to `range`, stopping before the first wall. */
export const lineTiles = (g: GridView, from: Pos, d: Dir, range: number): Pos[] => {
  const out: Pos[] = [];
  let cur = from;
  for (let i = 0; i < range; i += 1) {
    cur = add(cur, dirOffset[d]);
    if (!inBounds(g, cur) || !g.isFloor(cur)) break;
    out.push(cur);
  }
  return out;
};

/** The four orthogonal directions, in tie-break order. */
export const orthogonalDirs = ['n', 'e', 's', 'w'] as const satisfies readonly Dir[];

export const isOrthogonal = (d: Dir): boolean => (orthogonalDirs as readonly Dir[]).includes(d);

/**
 * Every tile reachable from `from` by at most `range` orthogonal steps over
 * non-wall tiles (`isFloor`), `from` included; units do not block the path.
 * A bounded breadth-first search; the result is in row-major order (by y,
 * then x). This is the line of sight of `single` targeting and of `room` in
 * a corridor: a Manhattan diamond with the tiles behind walls cut out.
 */
export const reachableWithin = (g: GridView, from: Pos, range: number): Pos[] => {
  const seen: boolean[] = [];
  for (let i = 0; i < g.width * g.height; i += 1) seen.push(false);
  const index = (p: Pos): number => p.y * g.width + p.x;
  const out: Pos[] = [];
  let frontier: Pos[] = [];
  if (inBounds(g, from)) {
    seen[index(from)] = true;
    out.push(from);
    frontier = [from];
  }
  for (let step = 0; step < range && frontier.length > 0; step += 1) {
    const next: Pos[] = [];
    for (const p of frontier) {
      for (const d of orthogonalDirs) {
        const q = add(p, dirOffset[d]);
        if (!inBounds(g, q) || seen[index(q)] === true || !g.isFloor(q)) continue;
        seen[index(q)] = true;
        out.push(q);
        next.push(q);
      }
    }
    frontier = next;
  }
  return out.sort(comparePos);
};

/** The first step of a shortest walk (`shortestWalk`) and the walk's length in steps. */
export type Walk = { readonly first: Dir; readonly length: number };

/**
 * A shortest walk from `from` to the tile `to`, or null when there is none:
 * a breadth-first search over legal steps (`canStep`, the corner rule
 * included), where units block every tile but `to` itself (the target's own
 * tile). Neighbours are expanded in `dirs` order and tiles visited in
 * breadth-first order, so among equally short walks the one whose first step
 * comes first in `dirs` order wins. When `to` is one legal step away the unit
 * is already beside it and there is nothing to walk: null too. A unit with no
 * walk (walled in, or every way blocked by units) gets null.
 */
export const shortestWalk = (g: GridView, from: Pos, to: Pos): Walk | null => {
  if (!inBounds(g, from) || !inBounds(g, to) || samePos(from, to)) return null;
  const open: GridView = { ...g, unitAt: (p) => (samePos(p, to) ? null : g.unitAt(p)) };
  const index = (p: Pos): number => p.y * g.width + p.x;
  const seen: boolean[] = [];
  for (let i = 0; i < g.width * g.height; i += 1) seen.push(false);
  seen[index(from)] = true;
  const queue: { readonly pos: Pos; readonly first: Dir; readonly length: number }[] = [];
  for (const d of dirs) {
    if (!canStep(open, from, d)) continue;
    const q = add(from, dirOffset[d]);
    if (samePos(q, to)) return null;
    seen[index(q)] = true;
    queue.push({ pos: q, first: d, length: 1 });
  }
  for (let head = 0; head < queue.length; head += 1) {
    const cur = queue[head];
    if (cur === undefined) break;
    for (const d of dirs) {
      if (!canStep(open, cur.pos, d)) continue;
      const q = add(cur.pos, dirOffset[d]);
      if (seen[index(q)] === true) continue;
      if (samePos(q, to)) return { first: cur.first, length: cur.length + 1 };
      seen[index(q)] = true;
      queue.push({ pos: q, first: cur.first, length: cur.length + 1 });
    }
  }
  return null;
};

/**
 * Every tile a unit at `from` can walk to, with the length and first step of
 * a shortest walk to each: one breadth-first search over legal steps
 * (`canStep`), expanded in `dirs` order, so each tile's walk is the one
 * `shortestWalk` finds. A tile holding a unit is recorded when first
 * reached (the walk that would end on it) but not walked through.
 * `maxDepth` bounds the walks (steps), or null for none. Read it with
 * `walkIn` (the `shortestWalk` answer) or `stepIn` (the raw entry).
 */
export type WalkTable = {
  readonly from: Pos;
  readonly width: number;
  readonly height: number;
  /** Steps from `from`, indexed `y × width + x`; -1 when not reached, 0 at `from`. */
  readonly length: readonly number[];
  /** The first step of the walk to each reached tile; null at `from` and unreached tiles. */
  readonly first: readonly (Dir | null)[];
  /** The reached tiles other than `from`, in breadth-first order (occupied ones included). */
  readonly order: readonly Pos[];
};

export const walkTable = (g: GridView, from: Pos, maxDepth: number | null): WalkTable => {
  const length: number[] = [];
  const first: (Dir | null)[] = [];
  for (let i = 0; i < g.width * g.height; i += 1) {
    length.push(-1);
    first.push(null);
  }
  const order: Pos[] = [];
  const table: WalkTable = { from, width: g.width, height: g.height, length, first, order };
  if (!inBounds(g, from)) return table;
  const index = (p: Pos): number => p.y * g.width + p.x;
  length[index(from)] = 0;
  const queue: Pos[] = [from];
  for (let head = 0; head < queue.length; head += 1) {
    const cur = queue[head];
    if (cur === undefined) break;
    const steps = length[index(cur)] ?? 0;
    if (maxDepth !== null && steps >= maxDepth) continue;
    for (const d of dirs) {
      const q = add(cur, dirOffset[d]);
      if (!inBounds(g, q) || !g.isFloor(q) || !cornerClear(g, cur, d) || length[index(q)] !== -1) continue;
      length[index(q)] = steps + 1;
      first[index(q)] = steps === 0 ? d : (first[index(cur)] ?? null);
      order.push(q);
      if (g.unitAt(q) === null) queue.push(q);
    }
  }
  return table;
};

/** The table's entry for `to`: the length and first step of the walk to it, or null when `to` is `from` or not reached. */
export const stepIn = (t: WalkTable, to: Pos): Walk | null => {
  if (!inBounds(t, to)) return null;
  const i = to.y * t.width + to.x;
  const d = t.first[i] ?? null;
  const length = t.length[i] ?? -1;
  return d === null || length < 1 ? null : { first: d, length };
};

/** `shortestWalk(g, t.from, to)` read from the table: its entry, but null when `to` is one step away (nothing to walk). */
export const walkIn = (t: WalkTable, to: Pos): Walk | null => {
  const e = stepIn(t, to);
  return e === null || e.length < 2 ? null : e;
};

export type ResolvedTarget = {
  readonly unit: string;
  readonly pos: Pos;
  /** Tiles beyond the first along a `line`, for falloff. 0 otherwise. */
  readonly distance: number;
};

/**
 * The units a targeting block hits, in order, for a user at `pos` facing
 * `facing`. `target` is the chosen unit for shape `single`. `sideOf` tells the
 * user's side from a unit id, so `who` can be applied. Throws on a shape that
 * needs a target but got none, a `single` target beyond Manhattan `range`
 * ('target out of range') or not reachable within it (`reachableWithin`:
 * 'target out of reach'), and a `line` facing a diagonal ('line skills fire
 * only north, east, south or west').
 */
export const resolveTargets = (
  g: GridView,
  t: Targeting,
  user: { readonly id: string; readonly pos: Pos; readonly facing: Dir },
  target: string | null,
  posOf: (unit: string) => Pos,
  isAlly: (unit: string) => boolean,
): ResolvedTarget[] => {
  const allowed = (unit: string): boolean => {
    if (t.shape === 'self' || t.who === undefined) return unit === user.id;
    if (t.who === 'everyone') return true;
    return t.who === 'allies' ? isAlly(unit) : !isAlly(unit);
  };
  const onTiles = (tiles: readonly Pos[]): ResolvedTarget[] => {
    const out: ResolvedTarget[] = [];
    for (const p of tiles) {
      const u = g.unitAt(p);
      if (u !== null && allowed(u)) out.push({ unit: u, pos: p, distance: 0 });
    }
    return out;
  };

  switch (t.shape) {
    case 'self':
      return [{ unit: user.id, pos: user.pos, distance: 0 }];
    case 'single': {
      if (target === null) throw new TargetingError('needs_target', 'shape single needs a target');
      const p = posOf(target);
      const range = t.range ?? 1;
      if (manhattan(user.pos, p) > range) throw new TargetingError('target_out_of_range', 'target out of range');
      if (!reachableWithin(g, user.pos, range).some((q) => samePos(q, p))) throw new TargetingError('target_out_of_reach', 'target out of reach');
      if (!allowed(target)) throw new TargetingError('target_not_allowed', 'target not allowed by who');
      return [{ unit: target, pos: p, distance: 0 }];
    }
    case 'line': {
      if (!isOrthogonal(user.facing)) throw new TargetingError('line_not_orthogonal', 'line skills fire only north, east, south or west');
      const out: ResolvedTarget[] = [];
      const tiles = lineTiles(g, user.pos, user.facing, t.range ?? 1);
      for (let i = 0; i < tiles.length; i += 1) {
        const p = tiles[i];
        if (!p) break;
        const u = g.unitAt(p);
        if (u === null) continue;
        if (allowed(u)) out.push({ unit: u, pos: p, distance: i });
        if (!t.pierce) break;
      }
      return out;
    }
    case 'room': {
      const room = g.roomAt(user.pos);
      const tiles = room === null ? (t.range === undefined ? [user.pos] : reachableWithin(g, user.pos, t.range)) : g.roomTiles(room);
      return onTiles(tiles);
    }
    case 'area': {
      const tiles = (t.cells ?? []).map(([dx, dy]) => add(user.pos, rotateOffset({ x: dx, y: dy }, user.facing)));
      return onTiles(tiles.filter((p) => inBounds(g, p)));
    }
    case 'floor': {
      const tiles: Pos[] = [];
      for (let y = 0; y < g.height; y += 1) for (let x = 0; x < g.width; x += 1) tiles.push({ x, y });
      return onTiles(tiles);
    }
  }
};

/** Why a targeting does not resolve: one of game-core's refusal codes (`RefusalCode`). */
export type TargetingRefusalCode = 'needs_target' | 'target_out_of_range' | 'target_out_of_reach' | 'target_not_allowed' | 'line_not_orthogonal';

export class TargetingError extends Error {
  override readonly name = 'TargetingError';
  readonly code: TargetingRefusalCode;
  constructor(code: TargetingRefusalCode, message: string) {
    super(message);
    this.code = code;
  }
}

/** A total order on positions: by y, then x. */
export const comparePos = (a: Pos, b: Pos): number => (a.y === b.y ? a.x - b.x : a.y - b.y);

